import { Box } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import { CardPin } from '@factorialco/f0-react/icons/app'
import {
  ApprovalsV2ApprovalTimelineEntry,
  PurchaseRequestFragment,
} from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import TimelineItem from 'modules/approvals_v2/components/TimelineItem'
import PurchaseRequestSummarySidebar from 'modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestSummarySidebar'

interface Props {
  purchaseRequest: PurchaseRequestFragment
  timelineEntries: ApprovalsV2ApprovalTimelineEntry[]
}

const WorkflowApprovalLayout = ({ purchaseRequest, timelineEntries }: Props) => {
  const approvalProcess = purchaseRequest?.approvalProcess
  const reviewRequests = approvalProcess?.reviewRequests || []

  const formatAlertDate = (timestamp: string) => {
    const date = stringToDate(timestamp, 'backend_datetime')
    return i18nDateToString(date, 'ui_short_with_time')
  }

  const isAutoRejected =
    approvalProcess?.decision === 'rejected' &&
    !approvalProcess?.decisionReason &&
    !reviewRequests.some((reviewRequest) => reviewRequest.decision === 'rejected')

  const automaticRejectionAlert =
    isAutoRejected && approvalProcess?.updatedAt ? (
      <OneAlert
        variant="critical"
        title={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.title'
        )}
        description={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.description',
          {
            date: formatAlertDate(approvalProcess.updatedAt),
          }
        )}
        icon={CardPin}
      />
    ) : null

  const getAlertForEntry = (entry: ApprovalsV2ApprovalTimelineEntry) => {
    const {
      actionGroup,
      status,
      decisionReason,
      time: timestamp,
      assigneeNames,
      employeeSegmentName,
    } = entry

    if (actionGroup !== 'approvals' || !decisionReason) {
      return undefined
    }

    const isRejected = status === 'rejected'
    const isChangesRequested = status === 'changesrequested'

    if (!isRejected && !isChangesRequested) {
      return undefined
    }

    const decision = isRejected ? 'rejected' : 'changes_requested'
    const matchingReviewRequest = reviewRequests.find(
      (reviewRequest) =>
        reviewRequest.decisionReason === decisionReason && reviewRequest.decision === decision
    )

    const reviewerTime = matchingReviewRequest?.reviewedAt || timestamp
    const reviewerName = assigneeNames?.[0] || employeeSegmentName || ''

    return (
      <OneAlert
        variant={isRejected ? 'critical' : 'warning'}
        title={
          isRejected
            ? i18n.t('procurement.purchase_requests.detail.timeline.rejected_alert.title', {
                reason: decisionReason,
              })
            : i18n.t('procurement.purchase_requests.detail.timeline.changes_requested_alert.title', {
              reason: decisionReason,
            })
        }
        description={`${reviewerName}, ${formatAlertDate(reviewerTime)}`}
        icon={CardPin}
      />
    )
  }

  const mainContent = (
    <Box gap="s24">
      {automaticRejectionAlert}
      <Box gap="s8">
        {timelineEntries.map((entry, index) => (
          <TimelineItem
            key={index}
            entry={entry}
            hasConnectingLine={index !== timelineEntries.length - 1}
            alert={getAlertForEntry(entry)}
          />
        ))}
      </Box>
    </Box>
  )

  const sideContent = <PurchaseRequestSummarySidebar purchaseRequest={purchaseRequest} />

  return <TwoColumnLayout sideContent={sideContent}>{mainContent}</TwoColumnLayout>
}

export default WorkflowApprovalLayout
